import { Icon } from '@iconify/react';
import { Button, Textarea, Tooltip } from '@nextui-org/react';
import React from 'react';
import { useDeletePost } from '../../../hooks/queries/prompts';
import type { Post } from '../../../types/agent';

interface ListPostsProps {
  posts: Post[];
  setPosts: (posts: Post[]) => void;
  showRunValidation: boolean;
  addMessagePair: () => void;
  promptId: number;
}

export default function ListPosts({
  posts,
  setPosts,
  showRunValidation,
  addMessagePair,
  promptId,
}: ListPostsProps) {
  const [hoveredPairId, setHoveredPairId] = React.useState<number | null>(null);
  const deletePost = useDeletePost();

  // Helper function to check if a post should be highlighted
  const shouldHighlight = (currentPost: Post) => {
    if (hoveredPairId === null) return false;

    if (!currentPost) return false;

    // If hovering over ASSISTANT
    if (currentPost.text_src === 'Assistant') {
      return (
        currentPost.id! === hoveredPairId ||
        currentPost.id! - 1 === hoveredPairId
      );
    } else {
      // If hovering over USER
      return (
        currentPost.id === hoveredPairId ||
        currentPost.id! + 1 === hoveredPairId
      );
    }
  };

  return (
    <div className="mb-6">
      <div className="mb-6">
        {posts.map((post, index) => (
          <div
            key={index}
            className={`space-y-4 mb-4 ${
              shouldHighlight(post) ? 'bg-yellow-100' : ''
            }`}
          >
            <Textarea
              isReadOnly={post.id ? true : false}
              isInvalid={
                showRunValidation &&
                !post.id &&
                post.text_src !== 'Assistant' &&
                !post.text.trim()
              }
              errorMessage={
                showRunValidation &&
                !post.id &&
                post.text_src !== 'Assistant' &&
                !post.text.trim()
                  ? 'This field is required'
                  : undefined
              }
              label={post.text_src === 'Assistant' ? 'Assistant' : 'User'}
              placeholder={
                post.text_src === 'Assistant'
                  ? 'Assistant response...'
                  : 'User message...'
              }
              value={post.text}
              onValueChange={(value) => {
                const newPosts = [...posts];
                newPosts[index].text = value;
                setPosts(newPosts);
              }}
              endContent={
                post.id &&
                !post.mandatory && (
                  <Tooltip content="Delete message pair to maintain user and assistant alternation">
                    <Button
                      isIconOnly
                      variant="light"
                      isLoading={
                        shouldHighlight(post) && deletePost.isPending
                          ? true
                          : false
                      }
                      onPress={async () => {
                        if (!post) return;
                        const firstPostId =
                          post.text_src === 'User' ? post.id : post.id! - 1;
                        const secondPostId =
                          post.text_src === 'User' ? post.id! + 1 : post.id;

                        await deletePost.mutateAsync({
                          promptId,
                          postIds: [firstPostId, secondPostId],
                        });

                        const newPosts = posts.filter((p) =>
                          post.text_src === 'User'
                            ? p.id !== post.id && p.id !== post.id! + 1
                            : p.id !== post.id && p.id !== post.id! - 1
                        );
                        setPosts(newPosts);
                      }}
                      onMouseEnter={() => setHoveredPairId(post.id!)}
                      onMouseLeave={() => setHoveredPairId(null)}
                    >
                      <Icon
                        icon="material-symbols:delete-outline"
                        className="w-4 h-4"
                      />
                    </Button>
                  </Tooltip>
                )
              }
            />
          </div>
        ))}

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="flat"
            onPress={addMessagePair}
            startContent={
              <Icon icon="material-symbols:add" className="w-4 h-4" />
            }
          >
            Add message pair
          </Button>
        </div>
      </div>
    </div>
  );
}
